import { useState, useEffect, useCallback } from 'react';
import { Table, TableHeader, TableBody, Column, Row, Cell } from 'react-aria-components';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getSupportPlansByChildIDForAdmin } from '@/api/getSupportPlans';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs/Breadcrumbs';
import Button from '@/components/Button/Button';
import { Pagination } from '@/components/Pagination/Pagination';
import { UserAtom, NotificationAtom } from '@/jotai/jotai';
import { formattedDate } from '@/logics/date/date';
import { Child } from '@/types/child';
import { SupportPlanListItem } from '@/types/supportPlan';

const DEFAULT_LIMIT = 15;

export const ChildSupportPlansPage = () => {
  const params = useParams<{ id: string }>();
  const childId = Number(params.id);
  const navigate = useNavigate();
  const { pathname: pathName } = useLocation();

  const [loginUser] = useAtom(UserAtom);
  const [_, setNotification] = useAtom(NotificationAtom);

  const [child, setChild] = useState<Child | null>(null);
  const [supportPlans, setSupportPlans] = useState<SupportPlanListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const breadcrumbs: BreadcrumbItem[] = [{ path: pathName, name: '個別支援計画一覧' }];

  // 個別支援計画一覧の取得
  const fetchSupportPlans = useCallback(
    async (page: number = 1) => {
      setIsLoading(true);
      try {
        const offset = (page - 1) * DEFAULT_LIMIT;
        const result = await getSupportPlansByChildIDForAdmin(
          childId,
          DEFAULT_LIMIT,
          offset,
          loginUser?.email
        );

        if (result.status === 200 && result.result) {
          setSupportPlans(result.result.support_plans);
          setTotalPages(
            result.result.total === 0 ? 1 : Math.ceil(result.result.total / DEFAULT_LIMIT)
          );
          setChild(result.result.child);
        } else {
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: '個別支援計画の取得に失敗しました',
            message: '個別支援計画の取得に失敗しました',
          });
        }
      } catch (error) {
        console.error('Error fetching support plans:', error);
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '個別支援計画の取得に失敗しました',
          message: 'ネットワークエラーが発生しました',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [childId, loginUser?.email, setNotification]
  );

  // 初期データ読み込み
  useEffect(() => {
    if (childId && loginUser) {
      fetchSupportPlans(1);
    }
  }, [childId, loginUser, fetchSupportPlans]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchSupportPlans(page);
  };

  const handleCreateNew = () => {
    navigate(`/admin/support-plan-drafts/create?childID=${childId}`);
  };

  const handleEditDraft = (draftId: number) => {
    navigate(`/admin/support-plan-drafts/${draftId}/edit`);
  };

  const handleEditPlan = (planId: number) => {
    navigate(`/admin/support-plans/${planId}/edit`);
  };

  const handleCreatePlanFromDraft = (draftId: number) => {
    navigate(`/admin/support-plans/create?draftID=${draftId}&childID=${childId}`);
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'in_progress':
        return '作成中';
      case 'pending_approval':
        return '承認待ち';
      case 'approved':
        return '承認済み';
      case 'rejected':
        return '差し戻し';
      default:
        return '未作成';
    }
  };

  const getStatusColorClass = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'bg-cyan-400 text-white';
      case 'pending_approval':
        return 'bg-yellow-400 text-white';
      case 'approved':
        return 'bg-green-400 text-white';
      case 'rejected':
        return 'bg-red-400 text-white';
      default:
        return 'bg-blue-400 text-white';
    }
  };

  if (isLoading && !child) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-gray-500">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="mb-4 flex flex-row">
        <Breadcrumbs item={breadcrumbs} />
      </div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {child ? `${child.last_name} ${child.first_name}` : ''}
        </h1>
      </div>
      <div className="mb-6 flex justify-end">
        <Button
          variant="solid-fill"
          size="lg"
          onClick={handleCreateNew}
        >
          項目を追加
        </Button>
      </div>
      <Table className="w-full border-collapse border border-gray-200">
        <TableHeader>
          <Column
            isRowHeader
            className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
          >
            計画案作成日
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            アセスメントシート
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            支援計画（案）
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">状態</Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            支援計画
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">状態</Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            支援会議録
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            実施日
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">議題</Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            モニタリング評価
          </Column>
        </TableHeader>
        <TableBody>
          {supportPlans.length === 0 ? (
            <Row>
              <Cell className="border border-gray-200 px-4 py-2 text-center text-gray-500">
                個別支援計画がありません
              </Cell>
              <Cell className="border border-gray-200 px-4 py-2"></Cell>
              <Cell className="border border-gray-200 px-4 py-2"></Cell>
              <Cell className="border border-gray-200 px-4 py-2"></Cell>
              <Cell className="border border-gray-200 px-4 py-2"></Cell>
              <Cell className="border border-gray-200 px-4 py-2"></Cell>
              <Cell className="border border-gray-200 px-4 py-2"></Cell>
              <Cell className="border border-gray-200 px-4 py-2"></Cell>
              <Cell className="border border-gray-200 px-4 py-2"></Cell>
              <Cell className="border border-gray-200 px-4 py-2"></Cell>
            </Row>
          ) : (
            supportPlans.map((plan) => (
              <Row key={plan.support_plan_draft_id}>
                <Cell className="border border-gray-200 px-4 py-2">
                  {formattedDate(new Date(plan.created_date), 'yyyy年MM月dd日')}
                </Cell>
                <Cell className="border border-gray-200 px-4 py-2">
                  {child?.assessment_sheet_image_url ? (
                    <Button
                      size="sm"
                      variant="text"
                      onClick={() => window.open(child.assessment_sheet_image_url, '_blank')}
                      className="text-xs"
                    >
                      プレビュー
                    </Button>
                  ) : (
                    <span className="text-gray-400">未設定</span>
                  )}
                </Cell>
                <Cell className="border border-gray-200 px-4 py-2">
                  <Button
                    size="sm"
                    variant="solid-fill"
                    onClick={() => handleEditDraft(plan.support_plan_draft_id)}
                    className="text-xs"
                  >
                    支援計画（案）編集
                  </Button>
                </Cell>
                <Cell className="border border-gray-200 px-4 py-2">
                  <span
                    className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getStatusColorClass(
                      plan.support_plan_draft_status
                    )}`}
                  >
                    {getStatusText(plan.support_plan_draft_status)}
                  </span>
                </Cell>
                <Cell className="border border-gray-200 px-4 py-2">
                  {plan.support_plan_id ? (
                    <Button
                      size="sm"
                      variant="solid-fill"
                      onClick={() => handleEditPlan(plan.support_plan_id!)}
                      className="text-xs"
                    >
                      支援計画編集
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCreatePlanFromDraft(plan.support_plan_draft_id)}
                      className="text-xs"
                    >
                      支援計画（案）を複製して作成
                    </Button>
                  )}
                </Cell>
                <Cell className="border border-gray-200 px-4 py-2">
                  <span
                    className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getStatusColorClass(
                      plan.support_plan_status || ''
                    )}`}
                  >
                    {getStatusText(plan.support_plan_status || '')}
                  </span>
                </Cell>
                <Cell className="border border-gray-200 px-4 py-2">
                  {/* 支援会議録は今後の支援会議録実装で実装予定 */}
                  <Button
                    size="sm"
                    variant="disabled"
                    disabled
                    className="text-xs"
                  >
                    支援会議記録
                  </Button>
                </Cell>
                <Cell className="border border-gray-200 px-4 py-2">
                  {/* 議題は今後の支援会議録実装で実装予定 */}
                  <span className="text-gray-400">-</span>
                </Cell>
                <Cell className="border border-gray-200 px-4 py-2">
                  {/* 実施日は今後の支援会議録実装で実装予定 */}
                  <span className="text-gray-400">-</span>
                </Cell>
                <Cell className="border border-gray-200 px-4 py-2">
                  {/* モニタリング評価は今後実装予定 */}
                  <Button
                    size="sm"
                    variant="disabled"
                    disabled
                    className="text-xs"
                  >
                    モニタリング報告書編集
                  </Button>
                </Cell>
              </Row>
            ))
          )}
        </TableBody>
      </Table>
      <div className="flex items-center justify-center gap-4 py-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default ChildSupportPlansPage;
